import { useCallback } from 'react';
import { LANGUAGES } from '../../templates/new/BasicsStep';
import { useLanguages, type Language } from '../../../lib/languages';

/**
 * The name a WhatsApp template version is shown by (1 November 2026): "English (US)", "Afrikaans", "Ikinyarwanda".
 *
 * The template editor's own list first, so a version reads the same here as where it was made. Then the language
 * catalogue (`GET /v1/languages`), by Meta's code or the code's base, in the language's own name; with the region
 * when the language has more than one Meta code ("Português (BR)"). Else Meta's code as it is.
 */
export function templateLanguageName(meta: string, catalogue: readonly Language[]): string {
  const lower = meta.toLowerCase();
  const listed = LANGUAGES.find((l) => l.value.toLowerCase() === lower);
  if (listed) return listed.label;
  const base = lower.split(/[-_]/)[0];
  const lang = catalogue.find((l) => (l.meta_codes ?? []).some((m) => m.toLowerCase() === lower))
    ?? catalogue.find((l) => l.code === base);
  if (!lang) return meta;
  const region = meta.includes('_') ? meta.slice(meta.indexOf('_') + 1) : '';
  return region && (lang.meta_codes ?? []).length > 1 ? `${lang.native_name} (${region})` : lang.native_name;
}

/** {@link templateLanguageName} over the catalogue as it has loaded so far. */
export function useTemplateLanguageName(): (meta: string) => string {
  const { languages } = useLanguages();
  return useCallback((meta: string) => templateLanguageName(meta, languages), [languages]);
}
